import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ReviewStatus } from "./useAdminReviews";

export const useReviewModeration = () => {
  const queryClient = useQueryClient();

  const updateReviewStatus = useMutation({
    mutationFn: async ({ reviewId, status }: { reviewId: string; status: ReviewStatus }) => {
      const { data, error } = await supabase
        .from('product_reviews')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', reviewId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
      queryClient.invalidateQueries({ queryKey: ['product-reviews'] });
      
      const statusText = variables.status === 'approved' ? 'approved' : 'rejected';
      toast({
        title: "Review Updated",
        description: `Review has been ${statusText} successfully.`,
      });
    },
    onError: (error) => {
      console.error('Error updating review:', error);
      toast({
        title: "Error",
        description: "Failed to update review status.",
        variant: "destructive",
      });
    },
  });

  const deleteReview = useMutation({
    mutationFn: async (reviewId: string) => {
      const { error } = await supabase
        .from('product_reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
      queryClient.invalidateQueries({ queryKey: ['product-reviews'] });
      
      toast({
        title: "Review Deleted",
        description: "Review has been permanently deleted.",
      });
    },
    onError: (error) => {
      console.error('Error deleting review:', error);
      toast({
        title: "Error",
        description: "Failed to delete review.",
        variant: "destructive",
      });
    },
  });

  const bulkUpdateStatus = useMutation({
    mutationFn: async ({ reviewIds, status }: { reviewIds: string[]; status: ReviewStatus }) => {
      const { error } = await supabase
        .from('product_reviews')
        .update({ status, updated_at: new Date().toISOString() })
        .in('id', reviewIds);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
      queryClient.invalidateQueries({ queryKey: ['product-reviews'] });
      
      const statusText = variables.status === 'approved' ? 'approved' : 'rejected';
      toast({
        title: "Reviews Updated",
        description: `${variables.reviewIds.length} reviews have been ${statusText}.`,
      });
    },
    onError: (error) => {
      console.error('Error bulk updating reviews:', error);
      toast({
        title: "Error",
        description: "Failed to update reviews.",
        variant: "destructive",
      });
    },
  });

  return {
    approveReview: (reviewId: string) => updateReviewStatus.mutate({ reviewId, status: 'approved' }),
    rejectReview: (reviewId: string) => updateReviewStatus.mutate({ reviewId, status: 'rejected' }),
    deleteReview: (reviewId: string) => deleteReview.mutate(reviewId),
    bulkApprove: (reviewIds: string[]) => bulkUpdateStatus.mutate({ reviewIds, status: 'approved' }),
    bulkReject: (reviewIds: string[]) => bulkUpdateStatus.mutate({ reviewIds, status: 'rejected' }),
    isLoading: updateReviewStatus.isPending || deleteReview.isPending || bulkUpdateStatus.isPending,
  };
};
